<template>
  <div class="kpi-tiles" :style="gridStyle">
    <div v-for="tile in props.tiles" :key="tile.label" class="kpi-tiles__tile" :class="{ 'kpi-tiles__tile--hidden': tile.isHidden }">
      <template v-if="!tile.isHidden">
        <div class="kpi-tiles__value">{{ tile.value }}</div>
        <div class="kpi-tiles__label">{{ tile.label }}</div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

/** One headline number and its caption. Values are pre-formatted by the calling report. */
export interface ReportKpiTile {
  label: string;
  value: string;
  /** Occupies a grid cell with no chrome so remaining tiles stay the same width. */
  isHidden?: boolean;
}

const props = defineProps<{
  tiles: ReportKpiTile[];
}>();

// Columns follow the tile count so 3, 4 or 5 tiles all fill the row evenly (tablet/mobile override this below).
const gridStyle = computed(() => ({ "--kpi-tiles-column-count": `${Math.max(1, props.tiles.length)}` }));
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";

// KPI tiles ==================================================================
.kpi-tiles {
  gap: 20px;
  display: grid;
  grid-template-columns: repeat(var(--kpi-tiles-column-count, 4), minmax(0, 1fr));

  &__tile {
    box-sizing: border-box;
    padding: 20px 22px;
    border: 1px solid #e4e7ec;
    border-radius: 11px;
    background: #fff;

    &--hidden {
      padding: 0;
      border: 0;
      background: transparent;
    }
  }

  &__value {
    margin-bottom: 4px;
    color: #102f41;
    font-family: $font-family;
    font-weight: 600;
    font-size: 34px;
    line-height: 40px;
  }

  &__label {
    color: #667085;
    font: 400 15px/19px sans-serif;
  }
}

// tablet + mobile ------------------------
@media (max-width: $tablet-max-width) {
  .kpi-tiles {
    gap: 14px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

// mobile ---------------------------------
@media (max-width: $mobile-max-width) {
  .kpi-tiles {
    &__tile {
      padding: 15px 16px;

      &--hidden {
        padding: 0;
      }
    }

    &__value {
      font-size: 27px;
      line-height: 32px;
    }
  }
}
</style>
